import React, { useEffect, useState } from 'react';
import StaffNavbar from '../components/StaffNavbar';
import StaffFansStatsCard from '../components/StaffFansStatsCard';
import Icon from '../components/Icon';
import apiService from '../services/api';
import { getSchoolRevenue } from '../utils/transactionRevenue';
// The funnel vocabulary, not the raw per-vendor status: `pending` was never in
// STATUS_FILTER_OPTIONS despite holding most rows, and `auth` vs `pending` only
// ever said which vendor reported the sale.
import { EXCLUDE_DEAD, FUNNEL_FILTER_OPTIONS, matchesFunnelStatus } from '../utils/funnel';
import {
  SOURCE_FILTER_OPTIONS,
  SOURCE_LABELS,
  getTransactionSource,
} from '../utils/transactionSource';
import '../styles/StaffPages.css';

const StaffTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]); // Store all transactions for filtering
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalPointsSpent: 0,
    totalTransactions: 0,
    avgTransaction: 0,
    loading: true
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  // Failed, refunded and voided transactions carry a vendor commission but no
  // sale that was kept, so they are out of every total unless selected.
  const [selectedStatus, setSelectedStatus] = useState(EXCLUDE_DEAD);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await apiService.getTransactions();
        // Handle both paginated (with .results) and non-paginated responses
        const transactionData = Array.isArray(response) ? response : (response.results || response || []);

        setAllTransactions(transactionData);
        setTransactions(transactionData);

        // Calculate stats
        const totalTransactions = transactionData.length;
        const totalPointsSpent = transactionData.reduce((sum, transaction) =>
          sum + (transaction.points_earned || 0), 0);
        // Total Reward is the sum of all commission amounts * school's commission split
        const totalReward = transactionData.reduce((sum, transaction) => {
          return sum + getSchoolRevenue(transaction);
        }, 0);
        const avgTransaction = transactionData.reduce((sum, transaction) => {
          return sum + (transaction.dollar_amount || 0);
        }, 0) / (totalTransactions || 1);

        setStats({
          totalRevenue: totalReward,
          totalPointsSpent,
          totalTransactions,
          avgTransaction,
          loading: false
        });
      } catch (error) {
        console.error('Error fetching transactions:', error);
        setTransactions([]);
        setStats(prevStats => ({ ...prevStats, loading: false }));
      }
    };

    fetchTransactions();
  }, []);

  // Generate month options from January 2026 onwards
  const generateMonthOptions = () => {
    const options = [{ value: 'all', label: 'All Transactions' }];
    const startDate = new Date(2026, 0); // January 2026
    const currentDate = new Date();
    // Go one month after current month
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);

    const current = new Date(startDate);
    while (current <= endDate) {
      const monthYear = `${current.getFullYear()}-${(current.getMonth() + 1).toString().padStart(2, '0')}`;
      const label = current.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value: monthYear, label });
      current.setMonth(current.getMonth() + 1);
    }

    return options;
  };

  // Filter transactions by selected month and source
  useEffect(() => {
    let filteredTransactions = [...allTransactions];

    if (selectedMonth !== 'all') {
      filteredTransactions = filteredTransactions.filter(transaction => {
        const transDate = new Date(transaction.created_at || transaction.auth_at || transaction.captured_at);
        const transMonthYear = `${transDate.getFullYear()}-${(transDate.getMonth() + 1).toString().padStart(2, '0')}`;
        return transMonthYear === selectedMonth;
      });
    }

    if (selectedStatus !== 'all') {
      filteredTransactions = filteredTransactions.filter(transaction =>
        matchesFunnelStatus(transaction, selectedStatus)
      );
    }

    if (selectedSource !== 'all') {
      filteredTransactions = filteredTransactions.filter(transaction => {
        const source = getTransactionSource(transaction);
        return source === selectedSource;
      });
    }

    setTransactions(filteredTransactions);

    const totalTransactions = filteredTransactions.length;
    const totalPointsSpent = filteredTransactions.reduce((sum, transaction) =>
      sum + (transaction.points_earned || 0), 0);
    const totalReward = filteredTransactions.reduce((sum, transaction) => {
      return sum + getSchoolRevenue(transaction);
    }, 0);
    const avgTransaction = filteredTransactions.reduce((sum, transaction) => {
      return sum + (transaction.dollar_amount || 0);
    }, 0) / (totalTransactions || 1);

    setStats({
      totalRevenue: totalReward,
      totalPointsSpent,
      totalTransactions,
      avgTransaction,
      loading: false
    });
  }, [selectedMonth, selectedSource, selectedStatus, allTransactions]);

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'Never' || dateString === 'N/A') {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate().toString().padStart(2, '0');
      return `${month}, ${day}`;
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      return '';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedTransactions = React.useMemo(() => {
    let filteredTransactions = [...transactions];

    // Apply search filter
    if (searchTerm) {
      filteredTransactions = filteredTransactions.filter(transaction => {
        const userFullName = transaction.user_name?.toLowerCase() || '';
        const userEmail = transaction.user_email?.toLowerCase() || '';
        const merchantName = transaction.merchant_name?.toLowerCase() || '';
        const search = searchTerm.toLowerCase();
        return userFullName.includes(search) ||
               userEmail.includes(search) ||
               merchantName.includes(search);
      });
    }

    // Sort filtered results
    if (!sortConfig.key) {
      return filteredTransactions;
    }

    return [...filteredTransactions].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'time':
        case 'date':
          // Both time and date columns sort by the full datetime
          aValue = new Date(a.created_at || a.auth_at || a.captured_at || 0);
          bValue = new Date(b.created_at || b.auth_at || b.captured_at || 0);
          break;
        case 'name':
          aValue = (a.user_name || '').toLowerCase();
          bValue = (b.user_name || '').toLowerCase();
          break;
        case 'merchant':
          aValue = (a.merchant_name || a.description || '').toLowerCase();
          bValue = (b.merchant_name || b.description || '').toLowerCase();
          break;
        case 'reward':
          // Sort by commission amount * school's commission split (the actual reward value shown)
          aValue = getSchoolRevenue(a);
          bValue = getSchoolRevenue(b);
          break;
        case 'price':
          aValue = a.dollar_amount || 0;
          bValue = b.dollar_amount || 0;
          break;
        case 'points':
          aValue = a.points_amount || 0;
          bValue = b.points_amount || 0;
          break;
        case 'type':
          aValue = a.transaction_type || '';
          bValue = b.transaction_type || '';
          break;
        case 'description':
          aValue = (a.description || '').toLowerCase();
          bValue = (b.description || '').toLowerCase();
          break;
        case 'amount':
          aValue = a.dollar_amount || 0;
          bValue = b.dollar_amount || 0;
          break;
        case 'user':
          aValue = (a.user_name || '').toLowerCase();
          bValue = (b.user_name || '').toLowerCase();
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'source':
          aValue = getTransactionSource(a).toLowerCase();
          bValue = getTransactionSource(b).toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [transactions, sortConfig, searchTerm]);

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="transactions" />
      <div className="admin-main">
        <div className="admin-content">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '8px'
          }}>
            <div>
              <h1 className="staff-page-header">Transactions</h1>
              <p className="staff-page-subheader">Real-time monitoring of all fan transactions and point awards.</p>
            </div>
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  fontFamily: '"Baikal Book"',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  minWidth: '180px'
                }}
              >
                {generateMonthOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                value={selectedSource}
                onChange={(e) => setSelectedSource(e.target.value)}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  fontFamily: '"Baikal Book"',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  minWidth: '180px'
                }}
              >
                {SOURCE_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '14px',
                  fontFamily: '"Baikal Book"',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  maxWidth: '100px'
                }}
              >
                {FUNNEL_FILTER_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '24px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '25%'
            }}>
              <StaffFansStatsCard
                title="Total Reward"
                value={stats.loading ? "..." : formatCurrency(stats.totalRevenue)}
                iconName="dollar-sign"
              />
            </div>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '25%'
            }}>
              <StaffFansStatsCard
                title="Points Awarded"
                value={stats.loading ? "..." : stats.totalPointsSpent.toLocaleString()}
                iconName="trending"
              />
            </div>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '25%'
            }}>
              <StaffFansStatsCard
                title="Total Transactions"
                value={stats.loading ? "..." : stats.totalTransactions.toLocaleString()}
                iconName="card"
              />
            </div>
            <div style={{
              flex: '1 1 200px',
              minWidth: '200px',
              maxWidth: '25%'
            }}>
              <StaffFansStatsCard
                title="Avg Transaction"
                value={stats.loading ? "..." : formatCurrency(stats.avgTransaction)}
                iconName="dollar-sign"
              />
            </div>
          </div>

          <h3 style={{
            fontFamily: 'Baikal Condensed',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '900',
            lineHeight: 'normal',
            marginTop: '32px',
            marginBottom: '4px',
          }}>
            {selectedMonth === 'all' ? 'Live Transaction Feed' : 'Transaction List'}
          </h3>

          <p style={{
            color: 'var(--Black, #000)',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '340',
            lineHeight: 'normal',
            opacity: '0.5',
            marginTop: '0',
            marginBottom: '16px'
          }}>
            {selectedMonth === 'all' ? 'Monitoring of all fan transactions and point awards.' : `Transactions for ${generateMonthOptions().find(opt => opt.value === selectedMonth)?.label || selectedMonth}`}
          </p>

          <div style={{
            display: 'flex',
            width: '400px',
            height: '44px',
            padding: '10px 14px',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '12px',
            border: '0.5px solid rgba(0, 0, 0, 0.50)',
            marginBottom: '24px'
          }}>
            <div style={{
              opacity: '0.5',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Icon name="search" size={18} />
            </div>
            <input
              type="text"
              placeholder="Search by name or merchant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '14px',
                background: 'transparent',
                color: 'var(--Black, #000)',
                fontFamily: '"Baikal Book"'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            width: '100%',
            maxWidth: '1270px',
            padding: '12px 18px',
            alignItems: 'center',
            borderRadius: '12px 12px 0 0',
            background: '#F8F8F8',
            backdropFilter: 'blur(22.5px)'
          }}>
            <div
              onClick={() => handleSort('time')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '8%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Time
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('name')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '14%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Name
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('merchant')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '17%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Merchant
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('reward')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '12%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Reward
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('price')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '14%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Spend
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('points')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '10%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Points
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('source')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '9%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Source
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('date')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '8%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Date
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('status')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '8%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Status
              <Icon name="up-down" size={12} />
            </div>
          </div>

          <div style={{ marginBottom: '8px' }}></div>

          {sortedTransactions.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px',
              background: '#F8F8F8',
              borderRadius: '12px',
              marginBottom: '8px'
            }}>
              <Icon name="card" size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
              <p style={{
                fontSize: '16px',
                fontFamily: '"Baikal Book"',
                color: '#666',
                margin: 0
              }}>
                No transactions found
              </p>
            </div>
          ) : (
            sortedTransactions.map((transaction, index) => (
              <div key={transaction.id || index} style={{
                display: 'flex',
                width: '100%',
                maxWidth: '1270px',
                height: '59px',
                padding: '18px',
                alignItems: 'center',
                borderRadius: '12px',
                background: '#F8F8F8',
                marginBottom: '8px'
              }}>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '8%',
                  minWidth: '0',
                  flexShrink: 0,
                  opacity: '0.7',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px'
                }}>
                  {formatTime(transaction.created_at || transaction.auth_at || transaction.captured_at)}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '14%',
                  minWidth: '0',
                  flexShrink: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px'
                }}>
                  {transaction.user_email || 'Unknown User'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '17%',
                  minWidth: '0',
                  flexShrink: 0,
                  opacity: '0.7',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px'
                }}>
                  {transaction.merchant_name || '—'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '12%',
                  minWidth: '0',
                  flexShrink: 0,
                  color: '#2E7D32',
                  fontWeight: '600',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px'
                }}>
                  {getSchoolRevenue(transaction) ? formatCurrency(getSchoolRevenue(transaction)) : '—'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '14%',
                  minWidth: '0',
                  flexShrink: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px',
                  fontWeight: '600'
                }}>
                  {transaction.dollar_amount ? formatCurrency(transaction.dollar_amount) : '—'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '10%',
                  minWidth: '0',
                  flexShrink: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px',
                  fontWeight: '600'
                }}>
                  {transaction.points_amount ? transaction.points_amount.toLocaleString() : '—'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '9%',
                  minWidth: '0',
                  flexShrink: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px',
                  opacity: '0.7'
                }}>
                  {SOURCE_LABELS[getTransactionSource(transaction)] ||
                    getTransactionSource(transaction)}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '8%',
                  minWidth: '0',
                  flexShrink: 0,
                  opacity: '0.7',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px'
                }}>
                  {formatDate(transaction.created_at || transaction.auth_at || transaction.captured_at)}
                </div>
                <div style={{
                  width: '8%',
                  minWidth: '0',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden'
                }}>
                  <span style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '12px',
                    display: 'flex',
                    width: 'max-content',
                    height: 'max-content',
                    padding: '4px 8px',
                    alignItems: 'center',
                    gap: '10px',
                    borderRadius: '100px',
                    background: (transaction.status === 'completed' || transaction.status === 'captured' || transaction.status === 'auth') ? '#D4F3D4' :
                              transaction.status === 'pending' ? '#FFF4CE' :
                              transaction.status === 'failed' ? '#FFE4E4' :
                              'rgba(0, 0, 0, 0.06)'
                  }}>
                    {(transaction.status === 'completed' || transaction.status === 'captured' || transaction.status === 'auth') ? 'complete' :
                     transaction.status === 'pending' ? 'pending' :
                     transaction.status === 'failed' ? 'failed' :
                     (transaction.status || 'unknown').toLowerCase()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffTransactions;